/**
 * PreLaunchGate.js — Backend-enforced private-preview passcode wall (v6.8.0).
 *
 * Wraps the ENTIRE app. Until you unlock with the shared access code, no app
 * screen renders AND no business `/api/*` data loads — the gate is enforced
 * server-side by `SiteGateMiddleware`, so the protection is real (the old
 * version was frontend-only and left the API wide open).
 *
 * How it works:
 *   1. On mount it asks the backend `GET /api/gate/status` → {enabled, unlocked}.
 *      - enabled=false (default / production-launch mode) → renders children,
 *        zero friction. The gate is controlled by backend env vars, NOT a
 *        REACT_APP_* flag, so the secret never ships in the JS bundle.
 *      - enabled=true & unlocked (valid `site_gate` cookie) → renders children.
 *      - enabled=true & locked → shows the passcode screen below.
 *   2. Submitting the code POSTs `/api/gate/unlock`; on success the backend
 *      sets a 30-day httpOnly+Secure cookie and we render the app.
 *
 * Operator setup (production env, NOT committed):
 *   SITE_GATE_ENABLED=true
 *   SITE_GATE_CODE=<your shared secret>          (or SITE_GATE_CODE_SHA256=<sha256 hex>)
 *   SITE_GATE_TTL_DAYS=30                         (optional, default 30)
 *
 * Layer this UNDER Cloudflare Zero Trust on your custom domain for
 * defense-in-depth — this gate additionally protects the raw *.emergent.host
 * URL, which Cloudflare Access can't see.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public legal pages that MUST stay reachable without the access code — app
// stores (Apple/Google) and prospective users need to read these before
// signing in, so they bypass the gate. (The backend API is still protected by
// SiteGateMiddleware regardless; these pages render only static content.)
const PUBLIC_GATE_BYPASS_PATHS = ['/privacy', '/terms'];

function isPublicBypassPath() {
  try {
    const p = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    return PUBLIC_GATE_BYPASS_PATHS.includes(p);
  } catch {
    return false;
  }
}

// Bare fetch (NOT the shared axios `api` instance) so the gate's pre-auth
// calls don't trip the app's 401/403 auth interceptors.
async function gateFetch(path, options = {}) {
  return fetch(`${BACKEND_URL}/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
}

export default function PreLaunchGate({ children }) {
  // 'checking' until /api/gate/status answers; then 'open' (render app) or
  // 'locked' (show passcode). Fail-safe to 'open' if status can't be read
  // (e.g. backend cold-start) so a transient blip never hard-locks the app —
  // the backend middleware is the real enforcer anyway.
  // Public legal pages (/privacy, /terms) start 'open' so a direct visit (or
  // app-store reviewer) is never blocked by the gate.
  const [phase, setPhase] = useState(isPublicBypassPath() ? 'open' : 'checking');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const checkStatus = useCallback(async () => {
    try {
      const res = await gateFetch('/gate/status', { method: 'GET' });
      const data = await res.json();
      if (!data.enabled || data.unlocked) setPhase('open');
      else setPhase('locked');
    } catch {
      setPhase('open'); // backend enforces; don't dead-lock the UI on a blip
    }
  }, []);

  useEffect(() => {
    if (isPublicBypassPath()) return; // legal pages bypass the gate entirely
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (phase === 'locked' && inputRef.current) inputRef.current.focus();
  }, [phase]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (submitting || code.length === 0) return;
      setSubmitting(true);
      setError('');
      try {
        const res = await gateFetch('/gate/unlock', {
          method: 'POST',
          body: JSON.stringify({ code }),
        });
        if (res.ok) {
          setCode('');
          setPhase('open');
          return;
        }
        if (res.status === 429) {
          setError('Too many attempts. Please wait a minute and try again.');
        } else {
          setError('Incorrect access code, please try again');
        }
        setCode('');
        if (inputRef.current) inputRef.current.focus();
      } catch {
        setError('Network error — please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [code, submitting],
  );

  // While we ask the backend, render nothing (a flash-free, indexable-nothing
  // blank). Crawlers see an empty shell, not the app.
  if (phase === 'checking') {
    return <div style={{ position: 'fixed', inset: 0, background: '#0a0a0f' }} data-testid="prelaunch-gate-checking" />;
  }
  if (phase === 'open') return children;

  // phase === 'locked'
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
      style={{
        background: '#0a0a0f',
        color: '#f5f5f7',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
      }}
      data-testid="prelaunch-gate"
    >
      <form onSubmit={handleSubmit} className="w-full max-w-sm" autoComplete="off">
        <div className="mb-8">
          <h1 className="text-xs font-semibold tracking-[0.25em] uppercase opacity-60">
            Restricted Access
          </h1>
          <p className="text-[11px] opacity-40 mt-2 leading-relaxed">
            This environment is in private preview. Please enter the access
            code to continue.
          </p>
        </div>
        <input
          ref={inputRef}
          type="password"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (error) setError('');
          }}
          placeholder="Access code"
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          disabled={submitting}
          aria-label="Access code"
          aria-invalid={!!error}
          aria-describedby={error ? 'prelaunch-error' : undefined}
          data-testid="prelaunch-gate-password-input"
          style={{
            width: '100%',
            padding: '14px 16px',
            fontSize: '14px',
            background: '#1a1a22',
            border: error ? '1px solid #e0526c' : '1px solid #2a2a35',
            borderRadius: '10px',
            color: '#f5f5f7',
            outline: 'none',
            transition: 'border-color 150ms ease',
          }}
        />
        {error && (
          <p
            id="prelaunch-error"
            role="alert"
            data-testid="prelaunch-gate-error"
            style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#ff7088',
              letterSpacing: '0.01em',
            }}
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || code.length === 0}
          data-testid="prelaunch-gate-submit"
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '12px 16px',
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            background: submitting || code.length === 0 ? '#2a2a35' : '#f5f5f7',
            color: submitting || code.length === 0 ? '#6a6a72' : '#0a0a0f',
            border: 'none',
            borderRadius: '10px',
            cursor: submitting || code.length === 0 ? 'not-allowed' : 'pointer',
            transition: 'background 150ms ease',
          }}
        >
          {submitting ? 'Checking…' : 'Enter'}
        </button>
      </form>
    </div>
  );
}
